<template>
  <v-content>
    <v-container fluid class="pa-0 fill-height flex-nowrap">
      <!-- Navigation drawer for md breakpoint and larger (hidden on sm and smaller) -->
      <v-navigation-drawer
        permanent
        width="465"
        color="#406d59"
        class="d-none d-md-flex"
      >
        <!-- Content wrapper to ensure the app doesn't have scroll -->
        <div class="d-flex flex-column flex-nowrap" style="height: 100vh;">
          <!-- DPV logo -->
          <v-img
            class="mx-auto my-8 flex-grow-0"
            src="../assets/dpvbrand.png"
            max-width="265"
          ></v-img>
          <!-- Company name -->
          <v-sheet color="#406d59" class="pa-4">
            <div class="headline" style="font-family: 'Oswald', sans-serif !important;font-weight: bold !important;">Company</div>
            <div v-if="company" class="title" style="font-family: 'Open Sans', sans-serif !important">{{ company.name }}</div>
          </v-sheet>
          <!-- Search field -->
          <v-text-field
            v-model="search"
            rounded
            dense
            outlined
            color="white"
            background-color="#bfc1ad"
            hide-details
            clearable
            class="mx-4 my-8 flex-grow-0"
            prepend-inner-icon="mdi-magnify"
            label="Search route"
          ></v-text-field>
          <!-- Routes list (has its own scroll) -->
          <div
            v-if="loading"
            class="d-flex align-center justify-center flex-grow-1"
          >
            <v-progress-circular indeterminate></v-progress-circular>
          </div>
          <v-list v-else class="overflow-y-auto" rounded>
            <v-list-item-group
              v-if="routes.length > 0"
              v-model="selected"
              mandatory
            >
              <v-list-item
                v-for="route in routes"
                color="#BFC1AD"
                :key="route.id"
                :value="route.id"
                @click="itemSelected"
              >
                <template v-slot:default="{ active, toggle }">
                  <v-list-item-action>
                    <v-switch
                      :input-value="active"
                      :true-value="route"
                      :color="route.color"
                      @click="toggle"
                    ></v-switch>
                  </v-list-item-action>
                  <v-list-item-content>
                    <v-list-item-title v-text="route.name"></v-list-item-title>
                  </v-list-item-content>
                </template>
              </v-list-item>
            </v-list-item-group>
            <v-list-item v-else>
              <v-list-item-content>
                <v-list-item-title class="text-center">
                  No routes found
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </div>
      </v-navigation-drawer>
      <v-container fluid class="pa-0 fill-height flex-column flex-nowrap">
        <!-- App bar for sm breakpoints and smaller (hidden on md and larger) -->
        <v-app-bar color="#406d59" class="d-md-none align-self-stretch">
          <v-toolbar-title>
            <v-img
              src="../assets/dpvbrand.png"
              contain
              max-height="48"
              max-width="100"
              position="left"
            ></v-img>
          </v-toolbar-title>
          <v-spacer></v-spacer>
          <v-text-field
            v-model="search"
            dense
            hide-details
            clearable
            outlined
            rounded
            color="white"
            background-color="#bfc1ad"
            class=""
            label="Search route"
          ></v-text-field>
          <v-btn icon>
            <v-icon>mdi-magnify</v-icon>
          </v-btn>
        </v-app-bar>
        <v-container fluid class="pa-0 fill-height d-block">
          <v-snackbar v-model="error" bottom color="error" :timeout="10000">
            {{ errorMsg }}
            <v-btn dark text @click="error = false">
              Close
            </v-btn>
          </v-snackbar>
          <!-- Routes chips -->
          <div v-if="loading">
            <v-progress-linear indeterminate></v-progress-linear>
          </div>
          <div v-else class="d-flex d-md-none justify-center routesChips">
            <v-chip-group
              v-if="routes.length > 0"
              v-model="selected"
              center-active
              mandatory
            >
              <template v-for="route in routes">
                <v-chip
                  v-if="selected === route.id"
                  :key="route.id"
                  :value="route.id"
                  :color="route.color"
                  class="elevation-3"
                  @click="itemSelected"
                >
                  {{ route.name }}
                </v-chip>
                <v-chip
                  v-else
                  :key="route.id"
                  :value="route.id"
                  color="white"
                  :text-color="route.color"
                  class="elevation-3"
                  @click="itemSelected"
                >
                  {{ route.name }}
                </v-chip>
              </template>
            </v-chip-group>
            <v-chip v-else disabled class="my-2 elevation-3">
              No routes found
            </v-chip>
          </div>
          <Map
            ref="map"
            :route="selectedRoute"
            :cars="cars"
            @activateCarPopUp="activateCarPopUp"
            @activateStopPopUp="activateStopPopUp"
          />
          <template v-if="selectedRoute">
            <CarPopUp
              v-model="showCarPopUp"
              :car="car"
              :route="selectedRoute"
            />
            <StopPopUp
              v-model="showStopPopUp"
              :stop="stop"
              :route-color="selectedRoute.color"
            />
          </template>
        </v-container>
      </v-container>
    </v-container>
  </v-content>
</template>

<script>
import Map from '../components/Map'
import CarPopUp from '../components/CarPopUp'
import StopPopUp from '../components/StopPopUp'
import API from '../services/api'
import { firestore } from '../services/firebase'
import decodePolyline from 'decode-google-map-polyline'

export default {
  name: 'Home',
  components: {
    Map,
    CarPopUp,
    StopPopUp
  },
  props: {
    companyId: {
      type: String,
      default: null,
      required: true
    }
  },
  data: () => ({
    loading: false,
    error: false,
    errorMsg: null,
    company: null,
    search: null,
    availableRoutes: [],
    selected: null,
    oldSelected: null,
    cars: [],
    showCarPopUp: false,
    car: null,
    showStopPopUp: false,
    stop: null
  }),
  computed: {
    routes: function() {
      return this.availableRoutes.filter(r => {
        if (!this.search) {
          return true
        }
        return (
          this.search &&
          r.name.toLowerCase().includes(this.search.toLowerCase())
        )
      })
    },
    selectedRoute: function() {
      return this.routes.find(r => r.id === this.selected)
    }
  },
  watch: {
    selected: function(newRoute, oldRoute) {
      if (!oldRoute) this.oldSelected = newRoute
      else this.oldSelected = oldRoute
    },
    selectedRoute: function() {
      this.unsubscribe()
      this.subscribe()
    }
  },
  beforeRouteEnter: (to, from, next) => {
    API.getCompanyInfo(to.params.companyId)
      .then(company => {
        console.log(company.data)
        next(vm => vm.setCompanyInfo(company.data))
      })
      .catch(() => next({ name: 'NotFound' }))
  },
  created: async function() {
    try {
      this.error = false
      this.loading = true
      const { data: routes } = await API.getRoutesByCompany(this.companyId)

      this.availableRoutes = routes.map(r => {
        for (let stop of r.stops) {
          stop.location = {
            lat: stop.location._latitude,
            lng: stop.location._longitude
          }
        }
        return {
          id: r.id,
          name: r.name,
          path: decodePolyline(r.encodePolyline),
          color: r.color,
          stops: r.stops
        }
      })
      //if (routes.lenght > 0) this.oldSelected = routes[0].id
    } catch (error) {
      this.errorMsg = error.message
      this.error = true
    } finally {
      this.loading = false
    }
  },
  methods: {
    setCompanyInfo: function(company) {
      this.company = company
    },
    itemSelected: async function() {
      if (this.selected === this.oldSelected)
        await this.$refs.map.setRouteOnMap(this.selectedRoute)
      this.oldSelected = this.selected
    },
    unsubscribe: function() {},
    subscribe: function() {
      if (!this.selectedRoute) return
      this.cars = []
      let unsubscribe = firestore()
        .collection('cars')
        .where('route', '==', this.selected)
        .onSnapshot(snapshot => {
          snapshot.docChanges().forEach(change => {
            let data = change.doc.data()
            if (change.type === 'added') {
              this.cars.push({
                id: change.doc.id,
                number: data.carNumber,
                location: {
                  lat: data.location.latitude,
                  lng: data.location.longitude
                },
                course: data.course,
                route: data.route,
                driver: data.driver,
                eta: data.eta
              })
            }
            if (change.type === 'modified') {
              let car = this.cars.find(car => car.id === change.doc.id)
              car.location = {
                lat: data.location.latitude,
                lng: data.location.longitude
              }
              car.course = data.course
              car.eta = data.eta
            }
            if (change.type === 'removed') {
              const carIndex = this.cars.findIndex(
                car => car.id === change.doc.id
              )
              if (carIndex !== null && carIndex !== undefined) {
                this.cars.splice(carIndex, 1)
              }
            }
          })
        })
      this.unsubscribe = unsubscribe
    },
    activateCarPopUp: function(carId) {
      this.car = this.cars.find(c => c.id === carId)
      this.showCarPopUp = true
    },
    activateStopPopUp: function(stopId) {
      this.stop = this.selectedRoute.stops.find(s => s.id === stopId)
      this.showStopPopUp = true
    }
  }
}
</script>

<style>

.routesChips {
  position: absolute !important;
  z-index: 1;
  width: 100%;
}

.routesChips > span {
  opacity: 1;
}
</style>
